/**
 * @fileoverview The week-later question about a plant check-up, on the Plants page:
 * "Did it turn out to be early blight?" Yes / Something else / Not sure.
 *
 * Asked once per check-up, about a week after it (the server says when:
 * DOCTOR_OUTCOME_ASK_AFTER_DAYS, for DOCTOR_OUTCOME_ASK_FOR_DAYS), the way
 * the first-photo share is asked (ShareMoments): from the bottom on a phone,
 * on the page rather than one card so it works in every view. Shown is
 * asked: the server is told when it appears, and it is never asked again,
 * answered or not. One at a time, and never over the share question.
 *
 * The answer and its date are kept on the check-up for Grow settings'
 * Recent check-ups. Nothing in the app claims an accuracy from them.
 *
 * Where: PlantGrid, once on the Plants page.
 * API: GET /plant-doctor/status (useDoctorStatus: its outcomes are the
 * questions due); POST /plant-doctor/checkups/:id/outcome-asked (as it
 * shows); POST /plant-doctor/checkups/:id/outcome { answer }.
 * Settings: DOCTOR_OUTCOME_ASK_AFTER_DAYS and DOCTOR_OUTCOME_ASK_FOR_DAYS
 * (the server's); PLANT_DOCTOR_STATUS_QUERY_KEY.
 * Words (en/es): plants.diagnose.outcome.* (title, body, yes, else, unsure,
 * once).
 * Watch out: it waits while ShareMoments has a first-photo question
 * pending, so two panels never stack.
 * Failures: a failure on our side (D31, lib/serverFailure) is reported to
 * Sentry; one saving the answer is also said in a toast with the standard
 * sentence. Other failures stay quiet, as the question is never asked again.
 *
 * @module DoctorMoments
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Plant } from '@/shared/types/plant';
import { answerOutcome, markOutcomeAsked, type OutcomeAnswer, type OutcomeQuestion } from '@/shared/api/diagnose';
import { useDoctorStatus } from '@/features/plant-list/lib/useDoctorStatus';
import { pendingFirstShare } from '@/features/share/lib/shareMoments';
import { PLANT_DOCTOR_STATUS_QUERY_KEY } from '@/config/tunables/plantList';
import { isServerFailure, reportServerFailure, serverFailureText } from '@/shared/lib/serverFailure';
import { toast } from '@/shared/components/ui/use-toast';

/** The answers, in the order they are offered. */
const ANSWERS: OutcomeAnswer[] = ['yes', 'else', 'unsure'];

/**
 * A likely problem as it reads inside a sentence: its first letter lowered,
 * unless the word is an abbreviation ("TMV").
 *
 * @param problem - As the check-up named it ("Early blight").
 * @returns "early blight".
 */
const inSentence = (problem: string): string => (/^[A-Z][a-z]/.test(problem) ? problem[0].toLowerCase() + problem.slice(1) : problem);

/**
 * The week-later question for one check-up at a time, on the Plants page.
 *
 * @param props.plants - The plants on the page; only their check-ups are asked about.
 */
export const DoctorMoments: React.FC<{ plants: Plant[] }> = ({ plants }) => {
  const { reactTranslate: t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  /** The doctor's status (GET /plant-doctor/status), whose outcomes are the questions due. */
  const { data } = useDoctorStatus();
  /** The question showing, or null. */
  const [asking, setAsking] = React.useState<OutcomeQuestion | null>(null);
  /** Check-ups asked about on this visit, so a refetch does not ask them again before the server knows. */
  const asked = React.useRef(new Set<number>());

  React.useEffect(() => {
    if (asking || !data) return;
    // The share question first: one panel at a time.
    if (pendingFirstShare() !== null) return;
    const onPage = new Set(plants.map((plant) => plant.plant_obj_id));
    const next = data.outcomes.find((o) => onPage.has(o.plantObjId) && !asked.current.has(o.checkupId));
    if (!next) return;
    asked.current.add(next.checkupId);
    setAsking(next);
    // Shown is asked: never again, answered or not.
    // Unseen by the gardener, so a failure on our side is only reported.
    void markOutcomeAsked(next.checkupId).catch((err: unknown) => {
      if (isServerFailure(err)) reportServerFailure(err, 'doctorMoments.asked');
    });
  }, [asking, data, plants]);

  /**
   * Keeps the answer, or just closes when there is none.
   *
   * @param answer - The tapped answer; null when the panel was closed.
   */
  const close = (answer: OutcomeAnswer | null): void => {
    const question = asking;
    setAsking(null);
    if (!question) return;
    const done = answer ? answerOutcome(question.checkupId, answer) : Promise.resolve();
    void done.catch((err: unknown) => {
      // The panel has closed; a failure on our side is said in a toast with
      // the standard sentence. Anything else stays quiet, as before.
      const words = serverFailureText(err, 'doctorMoments.answer');
      if (words) toast({ title: words, variant: 'destructive' });
    }).finally(() => {
      void queryClient.invalidateQueries({ queryKey: PLANT_DOCTOR_STATUS_QUERY_KEY });
    });
  };

  /** The check-up's day ("4 October"), read at noon so no zone moves it. */
  const checkedOn = asking
    ? new Date(`${asking.checkedOn}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })
    : '';

  return (
    <BottomPanel
      open={asking !== null}
      onOpenChange={(open) => { if (!open) close(null); }}
      title={asking ? t('plants.diagnose.outcome.title', { problem: inSentence(asking.problem) }) : ''}
      wrapTitle
    >
      <p className="text-sm text-muted-foreground">
        {asking ? t('plants.diagnose.outcome.body', { plant: asking.plantName, date: checkedOn }) : ''}
      </p>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {ANSWERS.map((answer) => (
          <Button key={answer} variant={answer === 'yes' ? 'default' : 'outline'} className="h-auto min-h-10 whitespace-normal px-2" onClick={() => close(answer)}>
            {t(`plants.diagnose.outcome.${answer}`)}
          </Button>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{t('plants.diagnose.outcome.once')}</p>
    </BottomPanel>
  );
};
